// Quelle: /opt/lilasprojekte/frontend/src/screens/uebersicht/hilfen.ts (Stand 2026-09-29)
// Geändert (SPEC 03 §9.2): Reihen KOMMUNE · JAHR · STUFE eines Verfahrens statt Phasen/Arten/
// Teams/Büros; `passt(v, f, ohne)` und die Facettenzahl „wenn angeschaltet“ unverändert im
// Prinzip. Gezählt werden hier VERFAHREN (Ordnungsdaten), keine Kinderdaten — außerhalb der
// T14-Regel (SPEC 03 §9.2, §13.3).
// ============================================================
// Der Rechenteil des Übersichtsblatts — reine Funktionen, kein React. Was man ohne Browser
// nachrechnen kann, steht hier; im Bauteil daneben bleibt nur, was gezeichnet wird. Der Grund
// ist Prüfbarkeit: die Facettenzahlen sind die Stelle, an der ein Filter still falsch sein
// kann, ohne dass es jemand sieht.
// ============================================================
import type { VerfahrenKurz } from "../../api/typen";

export type Reihe = "kommunen" | "jahre" | "stufen";
export type Stufe = "eingang" | "pruefung" | "auswertung";

export interface VfFilter {
  suche: string;
  kommunen: string[];
  jahre: string[];
  stufen: Stufe[];
}

export const LEERER_VF_FILTER: VfFilter = { suche: "", kommunen: [], jahre: [], stufen: [] };

export function istVfFilterLeer(f: VfFilter): boolean {
  return f.suche.trim() === "" && f.kommunen.length === 0 && f.jahre.length === 0 && f.stufen.length === 0;
}

/**
 * Die Stufe eines Verfahrens — abgeleitet, die API hat kein Feld dafür (R03-9):
 * noch kein Bogen → Eingang; nicht alle geprüft → Prüfung; sonst Auswertung.
 * („Fassung“ ist Rückstand und wird nie abgeleitet.)
 */
export function stufeVon(v: Pick<VerfahrenKurz, "pruefstand">): Stufe {
  if (v.pruefstand.gesamt === 0) return "eingang";
  if (v.pruefstand.geprueft < v.pruefstand.gesamt) return "pruefung";
  return "auswertung";
}

/** "MU26" → "2026" (die zwei Ziffern am Ende des Codes). */
export function jahrVon(code: string): string {
  return "20" + code.slice(-2);
}

/**
 * Passt das Verfahren — wahlweise OHNE eine der Reihen.
 *
 * Das `ohne` ist der ganze Trick hinter den Zahlen an den Chips: die Zahl an einem Chip ist die
 * Zahl der Treffer, WENN man ihn einschaltete, also unter allen anderen Filtern, aber ohne die
 * eigene Reihe. Eine leere Reihe heißt „alle“ und nicht „keins“.
 */
export function passt(v: VerfahrenKurz, f: VfFilter, ohne?: Reihe): boolean {
  if (ohne !== "kommunen" && f.kommunen.length > 0 && !f.kommunen.includes(v.kommune)) return false;
  if (ohne !== "jahre" && f.jahre.length > 0 && !f.jahre.includes(jahrVon(v.code))) return false;
  if (ohne !== "stufen" && f.stufen.length > 0 && !f.stufen.includes(stufeVon(v))) return false;
  // Die Suche bleibt IMMER scharf: sie ist keine Reihe, sondern die Frage, in der die Chips stehen.
  const wort = f.suche.trim().toLowerCase();
  if (wort && !`${v.code} ${v.titel} ${v.kommune}`.toLowerCase().includes(wort)) return false;
  return true;
}

export function traegt(v: VerfahrenKurz, reihe: Reihe, wert: string): boolean {
  if (reihe === "kommunen") return v.kommune === wert;
  if (reihe === "jahre") return jahrVon(v.code) === wert;
  return stufeVon(v) === wert;
}

/** Die Zahl am Chip: Treffer, WENN er anginge. */
export function facette(liste: readonly VerfahrenKurz[], f: VfFilter, reihe: Reihe, wert: string): number {
  return liste.filter((v) => passt(v, f, reihe) && traegt(v, reihe, wert)).length;
}

export function umschalten<T extends string>(liste: readonly T[], wert: T): T[] {
  return liste.includes(wert) ? liste.filter((x) => x !== wert) : [...liste, wert];
}

/** Verschiedene Werte einer Reihe, sortiert (Jahre absteigend: das neueste zuerst). */
export function werte(liste: readonly VerfahrenKurz[], reihe: Reihe): string[] {
  const s = new Set<string>();
  for (const v of liste) s.add(reihe === "kommunen" ? v.kommune : reihe === "jahre" ? jahrVon(v.code) : stufeVon(v));
  const a = [...s];
  if (reihe === "jahre") return a.sort().reverse();
  if (reihe === "stufen") return (["eingang", "pruefung", "auswertung"] as string[]).filter((x) => s.has(x));
  return a.sort((x, y) => x.localeCompare(y, "de"));
}

/**
 * Die Reihenfolge der Verfahren — im Übersichtsblatt UND im Umschalter der Kopfleiste (damit
 * beide Listen gleich stehen): das jüngste Jahr zuerst, darin nach Titel.
 */
export function sortiere(liste: readonly VerfahrenKurz[]): VerfahrenKurz[] {
  return [...liste].sort((a, b) => b.code.slice(-2).localeCompare(a.code.slice(-2)) || a.titel.localeCompare(b.titel, "de"));
}
